/**
 * Parse {@link IOspLoginInfo} from access_token JWT (SDK getLoginInfo subset used by token-seek).
 */

import type { IOspLoginInfo } from "@features/auth/compat/sdk-types";
import { jwtDecode } from "utils";
import { getAddress, isAddress } from "viem";

/** JWT payload fields used by email login + scheduled refresh cache update. */
export interface AccessTokenLoginJwt {
  aud?: string | string[];
  eoaAddress?: string;
  ospOwnerId?: string;
  chains?: Array<{
    ospAddress: string;
    sessionAddress?: string;
    status: "ACTIVE" | "INACTIVE" | string;
    profileId: string;
    chainId: number | string | bigint;
  }>;
}

export function decodeAccessTokenLoginJwt(
  accessToken: string,
): AccessTokenLoginJwt {
  return jwtDecode<AccessTokenLoginJwt>(accessToken);
}

export function isJwtAudMatchingApp(
  aud: AccessTokenLoginJwt["aud"],
  appId: string,
): boolean {
  if (aud === undefined) {
    return false;
  }
  if (Array.isArray(aud)) {
    return aud.some((entry) => String(entry) === appId);
  }
  return String(aud) === appId;
}

function normalizeEthAddress(address?: string): string {
  if (!address || !isAddress(address)) {
    return "";
  }
  return getAddress(address);
}

/** Map JWT payload → {@link IOspLoginInfo} (chains required for email login path). */
export function mapJwtToLoginInfo(payload: AccessTokenLoginJwt): IOspLoginInfo {
  if (!payload.chains?.length) {
    throw new Error("JWT missing chains");
  }
  return {
    eoaAddress: normalizeEthAddress(payload.eoaAddress),
    ospOwnerId: payload.ospOwnerId,
    chains: payload.chains.map((item) => ({
      ospAddress: normalizeEthAddress(item.ospAddress),
      sessionAddress: item.sessionAddress
        ? normalizeEthAddress(item.sessionAddress)
        : undefined,
      status:
        item.status === "ACTIVE" || item.status === "INACTIVE"
          ? item.status
          : "INACTIVE",
      profileId: item.profileId,
      chainId: Number(item.chainId),
    })),
  };
}
